import {
  useViewTasksSuspense,
  type ViewTasksRequest,
  type ViewTasksResponse
} from "@swivu/client-api";

import { Link } from "react-router-dom";
import ErrorBoundary from "./ErrorBoundary.js";
import SuspenseWithDelay from "./SuspenseWithDelay.js";
import { createContext, useContext } from "react";
import { groupBy } from "../utils/groupBy.js";
import { AccordionV2 } from "./AccordionV2.js";
import { Badge } from "./Badge.js";
import { decodeFA } from "../utils/decodeFA.js";
import { RichText } from "./RichText.js";

function isHexColorLightOrDark(color: string): "black" | "white" {
  let r: number, g: number, b: number;
  if (color.match(/^rgb/)) {
    const rgb = color.match(
      /rgb\((\d+),\s*(\d+),\s*(\d+)\)/
    ) as RegExpMatchArray;
    r = parseInt(rgb[1]);
    g = parseInt(rgb[2]);
    b = parseInt(rgb[3]);
  } else {
    const hex = color.replace("#", "");
    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);
  }
  const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));
  return hsp > 127.5 ? "black" : "white";
}
type ViewTasksVariables = ViewTasksRequest & {
  id: string;
  baseUrl: string;
};
type ViewTasksProps = ViewTasksVariables & {
  tasks: ViewTasksResponse;
};
const ViewTasksContext = createContext<ViewTasksProps | null>(null);
const ViewTasksProviderWrapper = ({
  children,
  ...props
}: ViewTasksVariables & { children: React.ReactNode }) => {
  return (
    <SuspenseWithDelay fallback={<ViewTasksSkeleton />}>
      <ErrorBoundary>
        <ViewTasksProvider {...props}>{children}</ViewTasksProvider>
      </ErrorBoundary>
    </SuspenseWithDelay>
  );
};

const ViewTasksProvider = ({
  children,
  ...props
}: ViewTasksVariables & { children: React.ReactNode }) => {
  const { data: tasks } = useViewTasksSuspense({
    variables: {
      ...props
    }
  });
  return (
    <ViewTasksContext.Provider value={{ ...props, tasks }}>
      {children}
    </ViewTasksContext.Provider>
  );
};

const ViewTasks = () => {
  return (
    <div className="space-y-6">
      <SuspenseWithDelay fallback={<ViewTasksSkeleton />}>
        <ErrorBoundary>
          <Tasks />
        </ErrorBoundary>
      </SuspenseWithDelay>
    </div>
  );
};

const ViewTasksSkeleton = () => {
  return (
    <ul className="animate-pulse space-y-8 pt-2">
      {Array.from({ length: 2 }).map((_, key) => (
        <li key={key} className="space-y-4">
          <div className="h-10 w-56 rounded-surface-2xs bg-mono-300 dark:bg-mono-800" />
          <dd className="grid gap-x-8 gap-y-4 pt-2 md:block md:space-y-4">
            {Array.from({ length: 2 * (key + 1) }).map((_, i) => (
              <dl
                key={i}
                className="flex h-28 overflow-hidden rounded-surface-md bg-white dark:bg-mono-900 shadow-card"
              >
                <div className="w-20 shrink-0 bg-mono-300 dark:bg-mono-800" />
                <div className="flex-1 space-y-3 p-6">
                  <div className="h-5 w-2/3 rounded-surface-2xs bg-mono-200 dark:bg-mono-800" />
                  <div className="h-4 w-1/3 rounded-surface-2xs bg-mono-200 dark:bg-mono-800" />
                </div>
              </dl>
            ))}
          </dd>
        </li>
      ))}
    </ul>
  );
};

type TasksResult = ViewTasksResponse[number];
type TasksResultsWithoutGroupValueNull = TasksResult & { _group_value: string };

const Tasks = () => {
  const { id, tasks, baseUrl } = ViewTasks.useViewTasks();

  const resultsWithoutNull = tasks.map((result) => {
    if (result._group_value === null) {
      result._group_value = `Not assigned to a task`;
    }
    return result;
  }) as TasksResultsWithoutGroupValueNull[];

  const results = groupBy(resultsWithoutNull, "_group_value");
  return (
    <div>
      <AccordionV2.PersistentRoot
        persistKey={`view-tasks-${id}`}
        defaultValue={["undefined"]}
      >
        {Object.entries(results).map(([key, tasks]) => (
          <AccordionV2.Item key={key} value={key ?? "undefined"}>
            <div className="sticky top-[0px] z-10 -mx-2 bg-mono-200 px-2 dark:bg-mono-950">
              {key !== "undefined" && (
                <AccordionV2.Trigger
                  className="mobile-pref-view-trigger w-full py-0"
                  headerClassName=" flex space-x-4 !border-b-0 py-0"
                >
                  <h3 className="mobile-pref-section-title flex items-center gap-3 py-5 text-2xl font-extrabold leading-tight md:text-3xl">
                    <span>{key}</span>
                    <Badge
                      count={tasks?.[0]._group_count ?? 0}
                      action="info"
                      className="mobile-pref-detail-badge static shrink-0"
                    />
                  </h3>
                </AccordionV2.Trigger>
              )}
            </div>
            <AccordionV2.Content>
              <dd className="accordion-content grid gap-x-8 gap-y-4 pb-6 pt-3 md:block md:space-y-4">
                {tasks.map((task) => {
                  const bg = task.color ?? "#ccc";
                  const textColor = isHexColorLightOrDark(bg);
                  const textShadow =
                    textColor === "white"
                      ? "var(--text-shadow-on-color)"
                      : "none";
                  return (
                    <dl
                      key={task.id}
                      className="relative overflow-hidden rounded-surface-md shadow-card"
                      style={{
                        backgroundColor: bg,
                        color: textColor,
                        textShadow
                      }}
                    >
                      <span aria-hidden className="top-gloss-large" />
                      <div className="relative flex items-center gap-4 p-5 md:gap-6 md:p-6">
                        <div className="min-w-0 flex-1">
                          <Link
                            to={`${baseUrl}${task.id}`}
                            className="mobile-pref-list-button group block rounded-surface-2xs"
                          >
                            <h5 className="mobile-pref-card-title text-xl font-extrabold leading-tight md:text-2xl">
                              {task.display_name}
                            </h5>
                          </Link>
                          <TaskDescription description={task.description} />
                          {task.budget_number && (
                            <p className="mobile-pref-card-meta pt-2 text-sm font-bold uppercase tracking-widest opacity-80">
                              Budget id: {task.budget_number}
                            </p>
                          )}
                        </div>
                        <Link
                          to={`${baseUrl}${task.id}`}
                          aria-label={`Open ${task.display_name}`}
                          className="group relative inline-flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white text-mono-900 shadow-pill transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.96] focus:outline-none focus-visible:ring-4 focus-visible:ring-white/60"
                          style={{ textShadow: "none" }}
                        >
                          <span aria-hidden className="top-gloss" />
                          <i
                            className={`relative fa-regular text-xl ${decodeFA(
                              "arrow-right"
                            )}`}
                          />
                        </Link>
                      </div>
                    </dl>
                  );
                })}
              </dd>
            </AccordionV2.Content>
          </AccordionV2.Item>
        ))}
      </AccordionV2.PersistentRoot>
    </div>
  );
};

const TaskDescription = ({ description }: { description?: string | null }) => {
  if (!description) return null;
  return (
    <div className="pt-2">
      <RichText
        className="mobile-pref-rich-text text-base font-bold leading-snug opacity-70 md:text-lg text-current! [&_*]:text-current!"
        html={description}
      />
    </div>
  );
};

const useViewTasks = () => {
  const context = useContext(ViewTasksContext);
  if (!context) {
    throw new Error("useViewTasks must be used within a ViewTasksProvider");
  }
  return context;
};

ViewTasks.Provider = ViewTasksProviderWrapper;
ViewTasks.useViewTasks = useViewTasks;
export default ViewTasks;
